// CGR twin dialogs: the modal flows behind Electronics International CGR-30P
// twin-engine recordings. One dialog shell hosts four flows: assigning each
// monitor unit to an engine at first upload, syncing the two engine recordings
// in time with an RPM preview canvas, editing an aircraft's saved monitor
// assignments, and adding a missing recording to a flight.
//
// tests/src/browser_cgr_twin_check.mjs asserts the element ids, class names,
// data attributes, aria labels, titles and button texts used here, so treat
// them as a contract.
import { escapeHtml } from "../core/html.js";

// Builds the shared modal card (eyebrow, title, close button, alert slot,
// Cancel + submit footer) around the given content and mounts it on the body.
function dialogShell(title: string, content: string) {
  const dialog = document.createElement("dialog");
  dialog.className = "upload-dialog cgr-twin-dialog";
  dialog.setAttribute("aria-label", title);
  dialog.innerHTML = `
    <form class="upload-dialog-card" method="dialog">
      <header>
        <div>
          <p class="eyebrow">CGR ENGINE RECORDINGS</p>
          <h2>${title}</h2>
        </div>
        <button type="button" class="icon-action upload-dialog-close" aria-label="Close">×</button>
      </header>
      ${content}
      <p class="upload-dialog-error" role="alert" hidden></p>
      <footer>
        <button type="button" class="secondary-action" data-cancel>Cancel</button>
        <button type="submit" class="primary-action">Continue upload</button>
      </footer>
    </form>
  `;
  document.body.append(dialog);
  dialog.querySelector("[data-cancel]")!.addEventListener("click", () => dialog.close());
  dialog.querySelector(".upload-dialog-close")!.addEventListener("click", () => dialog.close());
  return dialog;
}

// First-upload prompts run one at a time so two monitor files arriving
// together never stack their dialogs.
let assignmentQueue: Promise<any> = Promise.resolve();

// Asks which engine each newly seen CGR unit records. Resolves with a
// unitId -> role map, or null when the person closes the dialog.
export function chooseCgrEngineAssignments(payload: any): Promise<Record<string, string> | null> {
  const task = () => new Promise<Record<string, string> | null>((resolve) => {
    const units = payload.units ?? [];
    const labels = payload.labels ?? { left: "Left", right: "Right" };
    const unitFields = units.map((unit: any) => {
      const options = unit.kind === "shared"
        ? `<option value="shared">Shared CGR-30C</option>`
        : `<option value="">Select engine</option>`
          + `<option value="left">${escapeHtml(labels.left)} engine</option>`
          + `<option value="right">${escapeHtml(labels.right)} engine</option>`;
      return `
        <label class="cgr-twin-field">
          <span>${escapeHtml(unit.fileName)}<small>Unit ${escapeHtml(unit.unitId)}</small></span>
          <select data-unit="${escapeHtml(unit.unitId)}" required>${options}</select>
        </label>
      `;
    }).join("");
    const dialog = dialogShell("Assign engine monitors", `
      <p class="upload-dialog-copy">Choose which engine each CGR-30P records. We will remember these monitor assignments for this aircraft.</p>
      ${unitFields}
    `);
    let result: Record<string, string> | null = null;
    dialog.querySelector("form")!.addEventListener("submit", (event) => {
      event.preventDefault();
      const selects = [...dialog.querySelectorAll<HTMLSelectElement>("[data-unit]")];
      const selected = selects.map((select) => [select.dataset.unit!, select.value]);
      const roles = [...Object.values(payload.known ?? {}), ...selected.map((pair) => pair[1])];
      if (roles.length !== new Set(roles).size) {
        const error = dialog.querySelector<HTMLElement>("[role=alert]")!;
        error.hidden = false;
        error.textContent = "Assign only one monitor to each engine.";
        return;
      }
      result = Object.fromEntries(selected);
      dialog.close();
    });
    dialog.addEventListener("close", () => {
      dialog.remove();
      resolve(result);
    }, { once: true });
    dialog.showModal();
  });
  const next = assignmentQueue.then(task);
  assignmentQueue = next.catch(() => null);
  return next;
}

// Paints the "Sync engine data" control next to the flight report action for
// twin-parser flights, with a small status label (auto, manual, or missing).
export function renderCgrTwinSyncAction(entry: any, metadata: any, onApplied: () => Promise<void>) {
  const report = document.querySelector("#action-report");
  if (!report) return;
  let action = document.querySelector<HTMLElement>("#cgrTwinSyncAction");
  if (!action) {
    action = document.createElement("span");
    action.id = "cgrTwinSyncAction";
    action.className = "cgr-twin-action";
    report.before(action);
  }
  action.hidden = entry?.parser !== "ei-cgr-twin";
  if (action.hidden) return;
  const alignment = metadata?.cgrTwinAlignment;
  const manual = alignment?.sources?.some((source) => source.method === "manual");
  const status = manual ? "Manual offset" : alignment?.verified ? "Auto-synced" : "Not synced";
  const missing = alignment?.missing?.length ? ` · Missing ${alignment.missing.join(" / ")} engine` : "";
  action.replaceChildren();
  const label = document.createElement("small");
  label.textContent = status + missing;
  action.append(label);
  const button = document.createElement("button");
  button.type = "button";
  button.className = "secondary-action report-action button-with-icon";
  button.innerHTML = [
    `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">`,
    `<path d="M3 12a9 9 0 0 1 15.4-6.4L21 8M21 3v5h-5"/>`,
    `<path d="M21 12a9 9 0 0 1-15.4 6.4L3 16M3 21v-5h5"/>`,
    `</svg>`,
    `<span class="button-label">Sync engine data</span>`,
  ].join("");
  button.disabled = !entry.database?.flightId;
  button.title = button.disabled ? "Upload an engine recording to manage timing." : "";
  button.onclick = () => {
    void openSync(entry.database.flightId, onApplied);
  };
  action.append(button);
}

// Opens the timing dialog for one twin flight: loads the current alignment,
// draws the RPM preview, and lets the person nudge each 30P recording by whole
// seconds before saving the offsets and waiting for the reprocess job.
async function openSync(flightId: string, onApplied: () => Promise<void>) {
  const dialog = dialogShell("Sync engine data", `
    <div class="cgr-sync-recordings">
      <p class="upload-dialog-copy" data-description>Loading engine recordings...</p>
      <button type="button" class="cgr-sync-recordings-link" data-recordings-action>Manage recordings</button>
      <p class="upload-dialog-copy" data-missing-recordings hidden></p>
    </div>
    <div data-controls></div>
    <div class="cgr-sync-preview">
      <strong>RPM preview</strong>
      <div class="cgr-sync-legend"></div>
      <div class="cgr-sync-toolbar">
        <div class="cgr-sync-zoom">
          <button type="button" class="secondary-action" data-zoom-in aria-label="Zoom in RPM preview" title="Zoom in">+</button>
          <button type="button" class="secondary-action" data-zoom-out aria-label="Zoom out RPM preview" title="Zoom out">-</button>
          <button type="button" class="secondary-action" data-zoom-reset>Reset zoom</button>
        </div>
        <label>Preview range <select data-range>
          <option value="all">Full flight</option>
          <option value="start">First 10 minutes</option>
          <option value="end">Last 10 minutes</option>
          <option value="custom" disabled>Custom window</option>
        </select></label>
      </div>
      <canvas width="760" height="250" aria-label="Engine RPM alignment preview" aria-describedby="cgrSyncZoomHelp"></canvas>
      <div class="cgr-sync-range">
        <span id="cgrSyncZoomHelp">Drag across the graph to zoom into a time window.</span>
        <output data-window aria-label="Visible time window" aria-live="polite"></output>
      </div>
    </div>
    <p class="upload-dialog-copy">
      <span data-sync-help>Engine RPM changes can occur at different times. Use a known shared event.</span>
    </p>
    <p class="upload-dialog-copy">Applies to this flight only. Original timestamps are preserved.</p>
    <button type="button" class="secondary-action" data-reset>Reset adjustment</button>
    <p class="upload-dialog-copy">Reset uses the automatic match when available, otherwise the recorded clock.</p>
  `);
  const recordingsAction = dialog.querySelector<HTMLButtonElement>("[data-recordings-action]")!;
  recordingsAction.onclick = () => {
    void reviewCgrRecordings(flightId);
  };
  const submit = dialog.querySelector<HTMLButtonElement>("[type=submit]")!;
  submit.textContent = "Apply offset";
  submit.disabled = true;
  dialog.addEventListener("close", () => dialog.remove(), { once: true });
  dialog.showModal();
  const error = dialog.querySelector<HTMLElement>("[role=alert]")!;
  try {
    const response = await fetch(`/api/flights/${encodeURIComponent(flightId)}/cgr-timing`, { cache: "no-store" });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error);
    if (!dialog.isConnected) return;
    const { config, alignment, points, referencePoints = [] } = payload;
    const labels = payload.labels ?? { left: "Left", right: "Right", shared: "Shared" };
    const missingEngines = ["left", "right"].filter((role) => !config.sources.some((source) => source.role === role));
    if (missingEngines.length) {
      recordingsAction.textContent = "Add missing recording";
      recordingsAction.className = "secondary-action";
      const missing = dialog.querySelector<HTMLElement>("[data-missing-recordings]")!;
      missing.hidden = false;
      const missingLabels = missingEngines.map((role) => labels[role].toLowerCase()).join(" and ");
      missing.textContent = `Missing ${missingLabels} engine recording${missingEngines.length > 1 ? "s" : ""}.`;
    }
    const hasShared = alignment.sources.some((source) => source.role === "shared");
    const traces = ["left", "right"].flatMap((role, index) => [
      ...(alignment.sources.some((source) => source.role === role)
        ? [{ role, index, monitor: "30P", color: index === 0 ? "#2f88ff" : "#ff9f1c", dashed: false, points }]
        : []),
      ...(hasShared
        ? [{ role, index, monitor: "30C", color: index === 0 ? "#9bc8ff" : "#ffe0a3", dashed: true, points: referencePoints }]
        : []),
    ]);
    const legend = traces.map((trace) => {
      const style = `border-color:${trace.color};border-top-style:${trace.dashed ? "dashed" : "solid"}`;
      const suffix = trace.dashed ? " (fixed)" : "";
      return `<span><i style="${style}"></i>${escapeHtml(labels[trace.role])} ${trace.monitor}${suffix}</span>`;
    }).join("");
    dialog.querySelector(".cgr-sync-legend")!.innerHTML = legend;
    dialog.querySelector("[data-sync-help]")!.textContent = hasShared
      ? "Match each solid 30P trace to the same engine's dashed 30C trace. The 30C traces stay fixed while you adjust a 30P."
      : "Engine RPM changes can occur at different times. Use a known shared event.";
    const referenceLabel = alignment.reference === "shared"
      ? "Shared CGR-30C"
      : labels[alignment.reference] + " engine CGR-30P";
    dialog.querySelector("[data-description]")!.textContent = `Reference: ${referenceLabel}.`;
    const controls = dialog.querySelector<HTMLElement>("[data-controls]")!;
    const offsets: Record<string, number> = { ...config.offsets };
    const adjustable = alignment.sources.filter((source) => source.role !== alignment.reference && source.role !== "shared");
    const signedSeconds = (value: number) => (value > 0 ? `+${value}` : String(value));
    controls.innerHTML = adjustable.map((source) => {
      const label = escapeHtml(labels[source.role]);
      const method = source.method === "rpm-correlation"
        ? "Automatic RPM match"
        : source.method === "manual" ? "Manual offset" : "Clock alignment not verified";
      return `
        <div class="cgr-twin-field" data-role="${source.role}">
          <span>${label} engine<small>${method}</small></span>
          <div class="cgr-sync-stepper">
            <input class="cgr-sync-offset" type="text" role="spinbutton" aria-valuemin="-3600" aria-valuemax="3600"
              required autocomplete="off" spellcheck="false" value="${signedSeconds(source.offsetSeconds)}"
              aria-label="${label} offset seconds" aria-describedby="cgrSyncOffsetHelp">
            <div class="cgr-sync-step-buttons">
              <button type="button" data-step="1" aria-label="Increase ${label} offset by one second"
                title="Move one second later">▴</button>
              <button type="button" data-step="-1" aria-label="Decrease ${label} offset by one second"
                title="Move one second earlier">▾</button>
            </div>
          </div>
          <span>seconds</span>
        </div>
      `;
    }).join("");
    if (adjustable.length) {
      const offsetHelp = "+ moves later, - moves earlier. Use the arrows to adjust one second at a time.";
      controls.insertAdjacentHTML("beforeend", `<p class="upload-dialog-copy" id="cgrSyncOffsetHelp">${offsetHelp}</p>`);
    }
    if (!adjustable.length) controls.textContent = "Upload the other engine recording before adjusting its timing.";
    const canvas = dialog.querySelector("canvas")!;
    const rangeSelect = dialog.querySelector<HTMLSelectElement>("[data-range]")!;
    const max = Math.max(1, points.at(-1)?.elapsedSeconds ?? 0, referencePoints.at(-1)?.elapsedSeconds ?? 0);
    const minimumSpan = Math.min(10, max);
    let start = 0;
    let end = max;
    let selection: { pointerId: number; from: number; to: number } | null = null;
    const timeLabel = (value: number) => {
      const rounded = Math.round(value);
      return `${String(Math.floor(rounded / 60)).padStart(2, "0")}:${String(rounded % 60).padStart(2, "0")}`;
    };
    const setWindow = (from: number, to: number, preset = "custom") => {
      const span = Math.min(max, Math.max(minimumSpan, to - from));
      start = Math.max(0, Math.min(max - span, (from + to - span) / 2));
      end = start + span;
      rangeSelect.value = start === 0 && end === max ? "all" : preset;
      draw();
    };
    const updateStepper = (input: HTMLInputElement) => {
      const value = input.value.trim();
      const seconds = Number(value);
      const valid = /^[+-]?\d+$/.test(value) && Math.abs(seconds) <= 3600;
      input.setCustomValidity(valid ? "" : "Enter a whole number from -3600 to +3600 seconds.");
      input.setAttribute("aria-invalid", String(!valid));
      if (valid) input.setAttribute("aria-valuenow", String(seconds));
      else input.removeAttribute("aria-valuenow");
      for (const button of input.closest("[data-role]")!.querySelectorAll<HTMLButtonElement>("[data-step]")) {
        button.disabled = !valid || (Number(button.dataset.step) > 0 ? seconds >= 3600 : seconds <= -3600);
      }
      return valid;
    };
    // Repaints the preview: refreshes each stepper's validity and method label,
    // then draws the RPM grid, every trace shifted by its pending offset, and
    // the drag selection band while one is in progress. The plot area spans
    // x 45..745 and y 15..210 on the 760x250 canvas; 3000 RPM fills its height.
    const draw = () => {
      controls.querySelectorAll<HTMLInputElement>(".cgr-sync-offset").forEach(updateStepper);
      for (const source of adjustable) {
        const method = Object.hasOwn(offsets, source.role)
          ? "Manual offset"
          : source.automaticOffsetSeconds !== null ? "Automatic RPM match" : "Clock alignment not verified";
        controls.querySelector(`[data-role=${source.role}] small`)!.textContent = method;
      }
      const ctx = canvas.getContext("2d")!;
      ctx.clearRect(0, 0, 760, 250);
      dialog.querySelector<HTMLButtonElement>("[data-zoom-in]")!.disabled = end - start <= minimumSpan + 0.001;
      dialog.querySelector<HTMLButtonElement>("[data-zoom-out]")!.disabled = end - start >= max - 0.001;
      dialog.querySelector<HTMLButtonElement>("[data-zoom-reset]")!.disabled = start === 0 && end === max;
      dialog.querySelector("[data-window]")!.textContent = `${timeLabel(start)} - ${timeLabel(end)}`;
      ctx.strokeStyle = "#3b4857";
      ctx.lineWidth = 1;
      ctx.font = "11px system-ui";
      ctx.fillStyle = "#aab7c7";
      for (let n = 0; n <= 3; n++) {
        const y = 210 - n * 60;
        ctx.beginPath();
        ctx.moveTo(45, y);
        ctx.lineTo(745, y);
        ctx.stroke();
        ctx.fillText(String(n * 1000), 8, y + 4);
      }
      for (let n = 0; n <= 4; n++) ctx.fillText(timeLabel(start + (end - start) * n / 4), 45 + n * 170, 238);
      ctx.save();
      ctx.beginPath();
      ctx.rect(45, 15, 700, 195);
      ctx.clip();
      for (const trace of traces) {
        const original = alignment.sources.find((source) => source.role === trace.role);
        const delta = trace.dashed
          ? 0
          : (offsets[trace.role] ?? original.automaticOffsetSeconds ?? 0) - original.offsetSeconds;
        ctx.strokeStyle = trace.color;
        ctx.lineWidth = 2;
        ctx.setLineDash(trace.dashed ? [7, 5] : []);
        ctx.beginPath();
        let last = null;
        const interval = trace.points.length > 1
          ? Math.max(10, (trace.points[1].elapsedSeconds - trace.points[0].elapsedSeconds) * 2)
          : 10;
        for (const p of trace.points) {
          const value = p[`rpm${trace.index + 1}`];
          if (!Number.isFinite(value)) {
            last = null;
            continue;
          }
          const t = p.elapsedSeconds + delta;
          const x = 45 + (t - start) / (end - start || 1) * 700;
          const y = 210 - value / 3000 * 180;
          if (last === null || p.elapsedSeconds - last > interval) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          last = p.elapsedSeconds;
        }
        ctx.stroke();
      }
      if (selection) {
        ctx.fillStyle = "rgba(47,136,255,.2)";
        ctx.strokeStyle = "#9bc8ff";
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        const x = Math.min(selection.from, selection.to);
        const width = Math.abs(selection.to - selection.from);
        ctx.fillRect(x, 15, width, 195);
        ctx.strokeRect(x, 15, width, 195);
      }
      ctx.restore();
    };
    controls.addEventListener("input", (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>("[data-role]");
      if (!row) return;
      const input = row.querySelector("input")!;
      if (!updateStepper(input)) return;
      offsets[row.dataset.role!] = Number(input.value);
      draw();
    });
    const stepOffset = (input: HTMLInputElement, step: number) => {
      if (!updateStepper(input)) return;
      input.value = signedSeconds(Math.max(-3600, Math.min(3600, Number(input.value) + step)));
      input.dispatchEvent(new Event("input", { bubbles: true }));
    };
    controls.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-step]");
      if (button) stepOffset(button.closest("[data-role]")!.querySelector("input")!, Number(button.dataset.step));
    });
    controls.addEventListener("keydown", (event) => {
      const input = event.target as HTMLInputElement;
      if (!input.matches(".cgr-sync-offset") || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
      event.preventDefault();
      stepOffset(input, event.key === "ArrowUp" ? 1 : -1);
    });
    controls.addEventListener("focusout", (event) => {
      const input = event.target as HTMLInputElement;
      if (input.matches(".cgr-sync-offset") && input.validity.valid) input.value = signedSeconds(Number(input.value));
    });
    rangeSelect.addEventListener("change", () => {
      const preset = rangeSelect.value;
      const from = preset === "end" ? Math.max(0, max - 600) : 0;
      const to = preset === "start" ? Math.min(max, 600) : max;
      setWindow(from, to, preset);
    });
    const zoomFactors = [["[data-zoom-in]", 0.5], ["[data-zoom-out]", 2]] as const;
    for (const [selector, factor] of zoomFactors) {
      dialog.querySelector(selector)!.addEventListener("click", () => {
        const center = (start + end) / 2;
        const half = (end - start) * factor / 2;
        setWindow(center - half, center + half);
      });
    }
    dialog.querySelector("[data-zoom-reset]")!.addEventListener("click", () => setWindow(0, max, "all"));
    // Pointer-drag zoom: a primary-button drag inside the plot area paints a
    // selection band and, on release, narrows the visible window to it. Canvas
    // coordinates come from the displayed size so it works when CSS scales the
    // canvas down. Drags under 6 canvas pixels count as a click and do nothing.
    const pointerX = (event: PointerEvent) => {
      const bounds = canvas.getBoundingClientRect();
      return (event.clientX - bounds.left) / bounds.width * 760;
    };
    const clampX = (x: number) => Math.max(45, Math.min(745, x));
    canvas.addEventListener("pointerdown", (event) => {
      const x = pointerX(event);
      const bounds = canvas.getBoundingClientRect();
      const y = (event.clientY - bounds.top) / bounds.height * 250;
      if (!event.isPrimary || event.button !== 0 || x < 45 || x > 745 || y < 15 || y > 210) return;
      event.preventDefault();
      selection = { pointerId: event.pointerId, from: x, to: x };
      canvas.setPointerCapture(event.pointerId);
      draw();
    });
    canvas.addEventListener("pointermove", (event) => {
      if (selection?.pointerId !== event.pointerId) return;
      selection.to = clampX(pointerX(event));
      draw();
    });
    canvas.addEventListener("pointerup", (event) => {
      if (selection?.pointerId !== event.pointerId) return;
      const { from } = selection;
      const to = clampX(pointerX(event));
      selection = null;
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      if (Math.abs(to - from) < 6) {
        draw();
        return;
      }
      const span = end - start;
      setWindow(start + (Math.min(from, to) - 45) / 700 * span, start + (Math.max(from, to) - 45) / 700 * span);
    });
    for (const name of ["pointercancel", "lostpointercapture"]) {
      canvas.addEventListener(name, () => {
        selection = null;
        draw();
      });
    }
    dialog.querySelector("[data-reset]")!.addEventListener("click", () => {
      for (const key of Object.keys(offsets)) delete offsets[key];
      for (const source of adjustable) {
        const row = controls.querySelector(`[data-role=${source.role}]`)!;
        const value = source.automaticOffsetSeconds ?? 0;
        const input = row.querySelector("input")!;
        input.value = signedSeconds(value);
        input.setCustomValidity("");
      }
      draw();
    });
    submit.disabled = !adjustable.length;
    draw();
    dialog.querySelector("form")!.addEventListener("submit", async (event) => {
      event.preventDefault();
      submit.disabled = true;
      error.hidden = true;
      const closeButtons = [...dialog.querySelectorAll<HTMLButtonElement>("[data-cancel],.upload-dialog-close")];
      closeButtons.forEach((button) => {
        button.disabled = true;
      });
      recordingsAction.disabled = true;
      const preventClose = (cancelEvent: Event) => cancelEvent.preventDefault();
      dialog.addEventListener("cancel", preventClose);
      try {
        const saved = await fetch(`/api/flights/${encodeURIComponent(flightId)}/cgr-timing`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ offsets, expectedSources: JSON.stringify(config.sources) }),
        });
        const result = await saved.json();
        if (!saved.ok) throw new Error(result.error);
        submit.textContent = "Reprocessing...";
        for (let attempt = 0; attempt < 180; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const poll = await fetch(`/api/jobs/${encodeURIComponent(result.ingestJob.id)}`, { cache: "no-store" });
          const state = await poll.json();
          if (!poll.ok) throw new Error(state.error ?? "Could not check reprocessing.");
          const job = state.ingestJob ?? state.job ?? state;
          if (job.status === "failed") {
            throw new Error(job.error_message ?? "Reprocessing failed. Previous results are retained.");
          }
          if (job.status === "completed") {
            await onApplied();
            dialog.close();
            return;
          }
        }
        throw new Error("Reprocessing is still running. Reopen the flight after it completes.");
      } catch (failure) {
        error.textContent = (failure as Error).message;
        error.hidden = false;
        submit.disabled = false;
        submit.textContent = "Apply offset";
        closeButtons.forEach((button) => {
          button.disabled = false;
        });
        recordingsAction.disabled = false;
        dialog.removeEventListener("cancel", preventClose);
      }
    });
  } catch (failure) {
    error.hidden = false;
    error.textContent = (failure as Error).message;
  }
}

// Lets an aircraft owner change which engine each saved CGR unit records, and
// optionally reprocess chosen flights under the new assignments.
export async function openCgrMonitorAssignments(aircraftId: string) {
  const dialog = dialogShell("Monitor assignments", `<div data-assignments>Loading monitor assignments...</div>`);
  const submit = dialog.querySelector<HTMLButtonElement>("[type=submit]")!;
  submit.textContent = "Save assignments";
  submit.disabled = true;
  dialog.addEventListener("close", () => dialog.remove(), { once: true });
  dialog.showModal();
  const error = dialog.querySelector<HTMLElement>("[role=alert]")!;
  try {
    const response = await fetch(`/api/aircraft/${encodeURIComponent(aircraftId)}/cgr-monitors`, { cache: "no-store" });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error);
    if (!dialog.isConnected) return;
    const labels = payload.labels;
    const unitFields = Object.entries(payload.units).map(([unit, role]) => {
      const sides = role === "shared" ? ["shared"] : ["left", "right"];
      const options = sides.map((side) => {
        const text = `${escapeHtml(labels[side] ?? "Shared")}${side === "shared" ? " CGR-30C" : " engine"}`;
        return `<option value="${side}" ${role === side ? "selected" : ""}>${text}</option>`;
      }).join("");
      return `
        <label class="cgr-twin-field">
          <span>Unit ${escapeHtml(unit)}</span>
          <select data-unit="${escapeHtml(unit)}"><option value="">Not assigned</option>${options}</select>
        </label>
      `;
    }).join("");
    const flightFields = payload.flights.map((flight) => {
      const checkbox = `<input type="checkbox" data-flight="${escapeHtml(flight.id)}">`;
      return `<label class="cgr-reprocess-flight">${checkbox} ${escapeHtml(flight.title)}</label>`;
    }).join("");
    dialog.querySelector("[data-assignments]")!.innerHTML = `
      <p>Changes apply to future uploads. Existing flights keep their assignments unless selected below.</p>
      ${unitFields}
      <details>
        <summary>Also reprocess selected flights</summary>
        <p>Selected flights get the new assignments and their manual offsets are cleared. Original recordings are preserved.</p>
        ${flightFields}
      </details>
    `;
    submit.disabled = false;
    dialog.querySelector("form")!.addEventListener("submit", async (event) => {
      event.preventDefault();
      submit.disabled = true;
      error.hidden = true;
      try {
        const selects = [...dialog.querySelectorAll<HTMLSelectElement>("[data-unit]")];
        const assignments = Object.fromEntries(selects.map((select) => [select.dataset.unit!, select.value]));
        const checked = [...dialog.querySelectorAll<HTMLInputElement>("[data-flight]:checked")];
        const flightIds = checked.map((input) => input.dataset.flight);
        const saved = await fetch(`/api/aircraft/${encodeURIComponent(aircraftId)}/cgr-monitors`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ assignments, flightIds, expectedUnits: JSON.stringify(payload.units) }),
        });
        const result = await saved.json();
        if (!saved.ok) throw new Error(result.error);
        dialog.close();
        location.reload();
      } catch (failure) {
        error.textContent = (failure as Error).message;
        error.hidden = false;
        submit.disabled = false;
      }
    });
  } catch (failure) {
    error.textContent = (failure as Error).message;
    error.hidden = false;
  }
}

// Shows which recordings make up a twin flight and offers nearby unassigned
// recordings from the same aircraft to fill any missing engine or 30C slot.
async function reviewCgrRecordings(flightId: string) {
  const dialog = dialogShell("Flight recordings", `<div data-recordings>Loading recordings...</div>`);
  const submit = dialog.querySelector<HTMLButtonElement>("[type=submit]")!;
  submit.textContent = "Add recordings";
  submit.disabled = true;
  const error = dialog.querySelector<HTMLElement>("[role=alert]")!;
  dialog.addEventListener("close", () => dialog.remove(), { once: true });
  dialog.showModal();
  try {
    const response = await fetch(`/api/flights/${encodeURIComponent(flightId)}/cgr-sources`, { cache: "no-store" });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error);
    if (!dialog.isConnected) return;
    const { config, candidates, labels = { left: "Left", right: "Right", shared: "Shared" } } = payload;
    const currentSources = config.sources.map((source) => `
      <p><strong>${escapeHtml(labels[source.role])}:</strong> ${escapeHtml(source.path.split("/").at(-1))}</p>
    `).join("");
    const openRoles = ["left", "right", "shared"].filter((role) => !config.sources.some((source) => source.role === role));
    const missingFields = openRoles.map((role) => {
      const options = candidates
        .filter((source) => source.role === role)
        .map((source) => `<option value="${escapeHtml(source.path)}">${escapeHtml(source.label)}</option>`)
        .join("");
      const title = role === "shared" ? "Shared CGR-30C" : escapeHtml(labels[role]) + " engine";
      return `
        <label class="cgr-twin-field">
          <span>${title}</span>
          <select data-source><option value="">No recording selected</option>${options}</select>
        </label>
      `;
    }).join("");
    const hint = candidates.length
      ? "These nearby recordings could belong to this flight. Confirm using their original logs."
      : "Upload a missing recording to this aircraft to make it available here.";
    dialog.querySelector("[data-recordings]")!.innerHTML = `
      <p>Only choose recordings from this flight. Adding a source resets this flight's timing adjustments and reprocesses its data.</p>
      ${currentSources}
      ${missingFields}
      <p>${hint}</p>
    `;
    submit.disabled = !candidates.length;
    dialog.querySelector("form")!.addEventListener("submit", async (event) => {
      event.preventDefault();
      submit.disabled = true;
      error.hidden = true;
      try {
        const selects = [...dialog.querySelectorAll<HTMLSelectElement>("[data-source]")];
        const paths = selects.map((select) => select.value).filter(Boolean);
        const saved = await fetch(`/api/flights/${encodeURIComponent(flightId)}/cgr-sources`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ paths, expectedSources: JSON.stringify(config.sources) }),
        });
        const result = await saved.json();
        if (!saved.ok) throw new Error(result.error);
        dialog.close();
        location.reload();
      } catch (failure) {
        error.textContent = (failure as Error).message;
        error.hidden = false;
        submit.disabled = false;
      }
    });
  } catch (failure) {
    error.textContent = (failure as Error).message;
    error.hidden = false;
  }
}
